import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/RichTextEditor";
import { move, ButtonsField } from "@/components/admin/website/Fields";

const Box = ({ title, children }) => <div className="rounded-xl border bg-white p-4 space-y-3"><h4 className="font-semibold">{title}</h4>{children}</div>;
// as="div" for composite fields: a <label> forwards clicks to the first button inside it
const L = ({ label, children, as: W = "label" }) => <W className="block text-xs font-semibold text-slate-600">{label}<div className="mt-1 font-normal">{children}</div></W>;

const NavEditor = ({ nav, onChange }) => (
  <div className="space-y-2" data-testid="nav-editor">
    {nav.map((n, i) => (
      <div key={i} className="border rounded-lg p-2 space-y-2">
        <div className="flex gap-2">
          <Input value={n.label} placeholder="Label" onChange={(e) => onChange(nav.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} data-testid={`nav-${i}-label`} />
          <Input value={n.href} placeholder="/page or /#anchor" onChange={(e) => onChange(nav.map((x, j) => (j === i ? { ...x, href: e.target.value } : x)))} data-testid={`nav-${i}-href`} />
          <Button size="icon" variant="ghost" onClick={() => onChange(move(nav, i, -1))}><ArrowUp className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" onClick={() => onChange(move(nav, i, 1))}><ArrowDown className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" onClick={() => onChange(nav.filter((_, j) => j !== i))} data-testid={`nav-${i}-delete`}><Trash2 className="w-4 h-4" /></Button>
        </div>
        <div className="pl-6"><p className="text-[11px] text-slate-500 mb-1">Dropdown links (optional)</p>
          <ButtonsField value={n.children || []} onChange={(c) => onChange(nav.map((x, j) => (j === i ? { ...x, children: c } : x)))} testId={`nav-${i}-children`} /></div>
      </div>
    ))}
    <Button size="sm" variant="outline" onClick={() => onChange([...nav, { label: "", href: "", children: [] }])} data-testid="nav-add"><Plus className="w-3.5 h-3.5 mr-1" />Add menu item</Button>
  </div>
);

export const SiteSettingsForm = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/admin/website/site`, { headers }).then((r) => setS(r.data)); }, [headers]);
  if (!s) return <Loader2 className="w-5 h-5 animate-spin" />;
  const f = s.footer || {};
  const setF = (patch) => setS({ ...s, footer: { ...f, ...patch } });
  const save = async () => {
    setBusy(true);
    try { setS((await axios.put(`${API}/admin/website/site`, s, { headers })).data); toast.success("Website settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="site-settings">
      <Box title="Homepage">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.use_cms_home} onChange={(e) => setS({ ...s, use_cms_home: e.target.checked })} data-testid="use-cms-home" />
          Use the CMS "home" page as the homepage (when it is published). Untick to keep the current TheMatheMind 360 landing page.</label>
      </Box>
      <Box title="Header menu"><NavEditor nav={s.nav || []} onChange={(nav) => setS({ ...s, nav })} /></Box>
      <Box title="Header button">
        <div className="grid sm:grid-cols-2 gap-2">
          <L label="Label"><Input value={s.cta?.label || ""} onChange={(e) => setS({ ...s, cta: { ...s.cta, label: e.target.value } })} data-testid="cta-label" /></L>
          <L label="Link"><Input value={s.cta?.href || ""} onChange={(e) => setS({ ...s, cta: { ...s.cta, href: e.target.value } })} data-testid="cta-href" /></L>
        </div>
      </Box>
      <Box title="Brand colours">
        <div className="flex flex-wrap gap-4">
          {[["primary", "Primary"], ["accent", "Accent / buttons"], ["dark", "Dark sections & footer"]].map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm"><input type="color" value={s.theme?.[k] || "#000000"} onChange={(e) => setS({ ...s, theme: { ...s.theme, [k]: e.target.value } })} data-testid={`theme-${k}`} />{label}</label>
          ))}
        </div>
      </Box>
      <Box title="Footer">
        <L as="div" label="About text"><RichTextEditor value={f.text || ""} onChange={(v) => setF({ text: v })} minHeight={50} testId="footer-text" /></L>
        <div className="grid sm:grid-cols-2 gap-2">
          <L label="Email"><Input value={f.contact?.email || ""} onChange={(e) => setF({ contact: { ...f.contact, email: e.target.value } })} data-testid="footer-email" /></L>
          <L label="Phone"><Input value={f.contact?.phone || ""} onChange={(e) => setF({ contact: { ...f.contact, phone: e.target.value } })} data-testid="footer-phone" /></L>
          {["facebook", "instagram", "youtube"].map((k) => (
            <L key={k} label={`${k[0].toUpperCase()}${k.slice(1)} URL`}><Input value={f.social?.[k] || ""} onChange={(e) => setF({ social: { ...f.social, [k]: e.target.value } })} data-testid={`footer-${k}`} /></L>
          ))}
        </div>
        <L as="div" label="Extra footer links"><ButtonsField value={f.links || []} onChange={(links) => setF({ links })} testId="footer-links" /></L>
      </Box>
      <Button onClick={save} disabled={busy} data-testid="site-settings-save">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Save website settings</Button>
    </div>
  );
};
